import type { NodeWithPos, Range } from '@tiptap/core';
import { findParentNodeClosestToPos } from '@tiptap/core';
import type { Node, ResolvedPos } from '@tiptap/pm/model';
import type { Selection, Transaction } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';

import { Node_Type } from './../../../ricos-schema/legacy/wix/rich_content/v1';

export type ChangeListItemsIndent = (
  tr: Transaction,
  listItemsToIndent: NodeWithPos[],
  parentList: NodeWithPos
) => void | boolean;

export type ChangeListChildrenIndent = (tr: Transaction, parentList: NodeWithPos) => NodeWithPos[];

export const changeIndentationOfEveryListSeparately = (
  tr: Transaction,
  changeListChildrenIndent: ChangeListChildrenIndent
) => {
  const validNodeTypes = [Node_Type.BULLETED_LIST, Node_Type.ORDERED_LIST];
  const alreadyChangedIndent: string[] = [];

  tr.doc.nodesBetween(tr.selection.from, tr.selection.to, (node: Node, pos: number) => {
    if (!validNodeTypes.includes(node.type.name as Node_Type)) {
      return;
    }

    let needsToBeChangedIndent = false;

    node.forEach((child: Node, childPos: number) => {
      if (needsToBeChangedIndent) {
        return;
      }
      const paragraphNode = child.firstChild;

      if (!paragraphNode) {
        return;
      }
      const paragraphNodeStart = pos + childPos + 2;
      const paragraphNodeEnd = paragraphNodeStart + paragraphNode.nodeSize;

      needsToBeChangedIndent =
        paragraphNodeEnd >= tr.selection.from && paragraphNodeStart < tr.selection.to;
    });

    if (!needsToBeChangedIndent) {
      return;
    }

    // finds the pos in document by id in case some other item has already been sunk and the positions changed
    const newPos = getNewPos(tr, node.attrs.id);

    const parentListItem = findParentNodeClosestToPos(
      tr.doc.resolve(newPos),
      node => node.type?.name === Node_Type.LIST_ITEM
    );

    if (parentListItem && alreadyChangedIndent.includes(parentListItem.node.attrs.id)) {
      // if the parent list item has already been sunk, we don't need to sink this list or it's children
      return false;
    }

    const alreadyChangedIndentListItems = changeListChildrenIndent(tr, { node, pos: newPos });

    alreadyChangedIndentListItems.forEach(({ node }) => {
      alreadyChangedIndent.push(node.attrs.id);
    });
  });
  return true;
};

export const changeIndentationOfNotSelectedNodes = (
  tr: Transaction,
  notSelectedListItems: NodeWithPos[],
  changeListItemIndent: ChangeListItemsIndent
) => {
  let currParent: NodeWithPos | null = null;
  let siblings: NodeWithPos[] = [];

  notSelectedListItems.forEach(({ node }, i) => {
    const newPos = getNewPos(tr, node.attrs.id);
    const parentList = findParentList(tr.doc.resolve(newPos));
    if (!parentList) {
      return;
    }

    if (!currParent) {
      currParent = parentList;
    }

    if (currParent?.pos === parentList.pos) {
      siblings.push({ node, pos: newPos });
    } else {
      changeListItemIndent(tr, siblings, currParent);
      siblings = [];
      siblings.push({ node, pos: newPos });
      currParent = parentList;
    }

    if (i === notSelectedListItems.length - 1) {
      changeListItemIndent(tr, siblings, currParent);
    }
  });
};

export const getNotSelectedDescendantListItems = (
  tr: Transaction,
  listItemsInSelection: NodeWithPos[]
) => {
  const notSelectedDescendantListItems: NodeWithPos[] = [];
  const alreadyFoundParentPos: Range[] = [];
  let currentParentRange: null | Range = null;

  listItemsInSelection.forEach(listItem => {
    listItem.node.descendants((node: Node, pos: number) => {
      const startInDoc = pos + getNewPos(tr, listItem.node.attrs.id) + 1;
      if (node.type.name !== Node_Type.LIST_ITEM || startInDoc < tr.selection.to) {
        return;
      }

      const isNodeInFoundParents = alreadyFoundParentPos.some(
        ({ from, to }) => startInDoc > from && startInDoc < to
      );
      if (isNodeInFoundParents) {
        return false;
      }
      const shouldAddToList = startInDoc > tr.selection.to;

      if (shouldAddToList) {
        currentParentRange = { from: startInDoc, to: startInDoc + node.nodeSize };
        notSelectedDescendantListItems.push({ node, pos: startInDoc });
      }

      if (shouldAddToList && currentParentRange) {
        alreadyFoundParentPos.push(currentParentRange);
      }
    });
  });

  return notSelectedDescendantListItems;
};

export const getListItemsInSelection = (tr: Transaction, parentList: NodeWithPos) => {
  const listItemsInSelection: NodeWithPos[] = [];

  const firstListItemInSelection = findParentNodeClosestToPos(
    tr.selection.$from,
    node => node.type?.name === Node_Type.LIST_ITEM
  );
  if (!firstListItemInSelection) {
    return listItemsInSelection;
  }

  parentList.node.descendants((node: Node, pos: number) => {
    if (node.type.name !== Node_Type.LIST_ITEM) {
      return false;
    }
    const startInDoc = pos + parentList.pos + 1;
    if (startInDoc >= firstListItemInSelection.pos && startInDoc < tr.selection.to) {
      listItemsInSelection.push({ node, pos: startInDoc });
    }
  });
  return listItemsInSelection;
};

export const getIndentationsInSelection = (tr: Transaction) => {
  let smallestIndentation = Infinity;
  let biggestIndentation = -Infinity;
  const listNodes = getListItemsWithVisibleIndentation(tr);

  listNodes.forEach(({ node, visibleIndentation, pos }) => {
    node.forEach((childNode: Node, childPos: number) => {
      childNode.forEach((grandChildNode: Node, grandChildPos: number) => {
        if (
          grandChildNode.type.name === Node_Type.BULLETED_LIST ||
          grandChildNode.type.name === Node_Type.ORDERED_LIST
        ) {
          return;
        }
        const grandChildNodeStart = pos + childPos + grandChildPos + 2;
        const grandChildNodeEnd = grandChildNodeStart + grandChildNode.nodeSize;

        if (grandChildNodeStart <= tr.selection.to && grandChildNodeEnd >= tr.selection.from) {
          biggestIndentation = Math.max(biggestIndentation, visibleIndentation);
          smallestIndentation = Math.min(smallestIndentation, visibleIndentation);
        }
      });
    });
  });

  return { smallestIndentation, biggestIndentation };
};

export const getListItemsWithVisibleIndentation = (tr: Transaction) => {
  const listItemsWithVisibleIndent: { node: Node; visibleIndentation: number; pos: number }[] = [];

  const listTypes: string[] = [Node_Type.BULLETED_LIST, Node_Type.ORDERED_LIST];

  let listNodes: { offset: number; depth: number; node: Node }[] = [];

  tr.doc.descendants((node: Node, pos: number) => {
    if (!listTypes.includes(node.type.name)) {
      return;
    }

    const $pos = tr.doc.resolve(pos);
    if ($pos.depth === 0) {
      listNodes = [];
    }

    const listDepth = $pos.depth === 0 ? 0 : $pos.depth / 2;
    //this removes list nodes of the same depth (siblings of the same parent) or deeper (children of previous nodes)
    listNodes = listNodes.filter(prevNode => prevNode.depth < listDepth);

    listNodes.push({
      offset: node.attrs.offset,
      depth: listDepth,
      node,
    });

    const offsetSum = listNodes.map(listNode => listNode.offset).reduce((acc, sum) => acc + sum, 0);

    const level = listNodes.length - 1;
    const visibleIndentation = offsetSum + level;

    listItemsWithVisibleIndent.push({ node, visibleIndentation, pos });
  });
  return listItemsWithVisibleIndent;
};

const getNewPos = (tr: Transaction, id: string) => {
  let newPos = 0;
  tr.doc.descendants((node: Node, pos: number) => {
    if (node.attrs.id === id) {
      newPos = pos;
    }
  });
  return newPos;
};

export const findParentList = ($pos: ResolvedPos) => {
  const validNodeTypes = [Node_Type.BULLETED_LIST, Node_Type.ORDERED_LIST];
  return findParentNodeClosestToPos($pos, node =>
    validNodeTypes.includes(node.type.name as Node_Type)
  );
};

export const fixTextSelection = (tr: Transaction, selection: Selection) => {
  tr.setSelection(
    TextSelection.between(tr.doc.resolve(selection.from), tr.doc.resolve(selection.to))
  );
};
